import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";

import { useAuthMe, useCart } from "@/api/queries";
import { useTenantBranding } from "@/api/queries/branding";
import { AccountMenu } from "@/components/AccountMenu";
import { BrandLogo } from "@/components/BrandLogo";
import { LangSwitcher } from "@/components/LangSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { FeaturesDropdown } from "@/components/FeaturesDropdown";
import { ServicesDropdown } from "@/components/ServicesDropdown";
import { TenantBrandMark } from "@/components/TenantBrandMark";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { cx } from "@/lib/utils";
import { isPageVisible } from "@/shell/toggleablePages";
import { useShellNav, type PrimaryLink } from "@/shell/useShellNav";

/**
 * Single sticky header used on every customer page.
 *
 * Layout:
 *   [ Brand ] [ Primary nav: marketing links ] [ (theme · lang) · Cart · AccountMenu ]
 *
 * Theme and language sit in the header only for **anonymous** visitors.
 * A signed-in user finds them inside the account menu, which is the same
 * place on every shell — the storefront used to keep them in the header
 * while the admin shell kept them in the menu, so the same preference
 * lived in two spots depending on which page you were on.
 */
interface HeaderProps {
  left?: { label: string; to?: string };
  right?: "with-menu" | "lang-only";
  showNav?: boolean;
}

export function Header({
  right = "with-menu",
  showNav = true,
}: HeaderProps): JSX.Element {
  const { t } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const [mobileOpen, setMobileOpen] = useState(false);
  const shellNav = useShellNav();
  // Tenant subdomains render the tenant's own logo + name in the
  // header; platform / platform-admin shells keep the generic
  // Forgmatic wordmark via <BrandLogo>. We only fetch on tenant
  // shells to avoid an extra request on the marketing site.
  const isTenantShell = shellNav.shell === "tenant";
  const branding = useTenantBranding({ enabled: isTenantShell });
  // Signed-in → theme + language live in the account menu. While
  // ``/auth/me`` is still resolving we render neither, so the controls
  // don't appear and then vanish a tick later. Without a menu to hold
  // them (``right="lang-only"``) they always stay in the header.
  const me = useAuthMe();
  const showHeaderPrefs = right !== "with-menu" || (!me.isPending && !me.data);

  return (
    <header
      className="st-hdr sticky top-0 z-40 w-full border-b border-border bg-bg/85 backdrop-blur"
      data-testid="site-header"
    >
      <div className="st-hdr-in mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-7 py-3.5">
        <Link
          to={`/${lang}`}
          className="inline-flex items-center gap-2 text-ink hover:text-ink-2"
          data-testid="header-brand"
          // KAN-1644/KAN-1681 — obchod bez jména (i během načítání brandingu)
          // nenese v odkazu nic; prázdný odkaz je pro čtečku nečitelný.
          aria-label={
            isTenantShell && !branding.data?.name?.trim()
              ? t("nav.home")
              : undefined
          }
        >
          {isTenantShell ? (
            <TenantBrandMark
              branding={branding.data}
              logoTestId="header-tenant-logo"
              nameTestId="header-tenant-name"
            />
          ) : (
            <BrandLogo />
          )}
        </Link>
        {showNav && (
          <PrimaryNav
            mobileOpen={mobileOpen}
            onMobileClose={() => setMobileOpen(false)}
          />
        )}
        <div className="flex items-center gap-2">
          {showNav && (
            <button
              type="button"
              aria-label={t("nav.toggleMobile")}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-ink-2 hover:bg-surface-2 sm:hidden"
              data-testid="header-mobile-toggle"
            >
              {mobileOpen ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              ) : (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          )}
          {showHeaderPrefs && (
            <>
              <div className="hidden sm:block">
                <ThemeToggle />
              </div>
              <LangSwitcher />
            </>
          )}
          {showNav && shellNav.showCart && <CartButton lang={lang} />}
          {right === "with-menu" && <AccountMenu />}
        </div>
      </div>
    </header>
  );
}

function CartButton({ lang }: { lang: string }): JSX.Element {
  const { t } = useTranslation("common");
  const cart = useCart();
  const count = cart.data?.items.reduce((n, i) => n + i.lines.length, 0) ?? 0;

  return (
    <Link
      to={`/${lang}/cart`}
      className="relative inline-flex items-center rounded-md px-2 py-1 text-sm text-ink-2 transition-colors hover:text-ink"
      data-testid="nav-cart"
      aria-label={t("nav.cart")}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="h-5 w-5"
        aria-hidden="true"
      >
        <path d="M1 1.75A.75.75 0 0 1 1.75 1h1.628a1.75 1.75 0 0 1 1.734 1.519l.049.352A2 2 0 0 1 7 4h10.25a.75.75 0 0 1 .73.927l-1.5 6A.75.75 0 0 1 15.75 11H7.727l.073.52A.25.25 0 0 0 8.047 12H16a.75.75 0 0 1 0 1.5H8.047a1.75 1.75 0 0 1-1.727-1.466L4.81 2.715A.25.25 0 0 0 4.563 2.5H1.75A.75.75 0 0 1 1 1.75ZM6 15.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm9.5-1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" />
      </svg>
      {count > 0 && (
        <span
          className="mono ml-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-ink px-1 text-[10px] tabular-nums text-bg"
          aria-label={t("nav.cartBadge", { count })}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

function PrimaryNav({
  mobileOpen,
  onMobileClose,
}: {
  mobileOpen: boolean;
  onMobileClose: () => void;
}): JSX.Element {
  const { t } = useTranslation("common");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  const { pathname } = useLocation();
  const shellNav = useShellNav();
  // Filter the tenant nav by per-tenant visibility. ``pageKey`` is
  // only set on tenant-shell links, so platform / platform-admin
  // shells fall through unaffected (every link returns true).
  const isTenant = shellNav.shell === "tenant";
  const branding = useTenantBranding({ enabled: isTenant });
  const visibility = branding.data?.pages_visibility;
  // Dokud se branding obchodu nenačte, neví se, které stránky obchod vypnul;
  // první vykreslení pak ukazovalo obecnou hlavičku („Naše služby“) a po
  // chvíli ji překreslilo (KAN-1796). Nabídka tenanta se kreslí až s daty.
  const brandingPending = isTenant && !branding.data && !branding.isError;
  const visibleLinks = brandingPending
    ? []
    : shellNav.primaryLinks.filter(
        (l) => !l.pageKey || isPageVisible(visibility, l.pageKey),
      );
  const showServices =
    !brandingPending &&
    shellNav.showServicesDropdown &&
    isPageVisible(visibility, "services");
  // Nesahá do ``visibility``: ``pages_visibility`` je per-tenant mapa a
  // tahle položka na tenantím shellu nikdy není. Viz ``showFeaturesDropdown``.
  const showFeatures = shellNav.showFeaturesDropdown;

  const renderLink = (link: PrimaryLink, extraClass = ""): JSX.Element => {
    const href = `/${lang}${link.to}`;
    const active = pathname.startsWith(href);
    return (
      <Link
        key={link.testId}
        to={href}
        onClick={onMobileClose}
        className={cx(
          "rounded-md px-2 py-1 text-sm transition-colors",
          active ? "font-medium text-ink" : "text-ink-2 hover:text-ink",
          extraClass,
        )}
        aria-current={active ? "page" : undefined}
        data-testid={link.testId}
      >
        {t(shellNav.shell === "platform" && link.i18nKey === "forBusiness" ? "platformBuyerNav" : `nav.${link.i18nKey}`)}
      </Link>
    );
  };

  return (
    <>
      <nav
        className="hidden items-center gap-5 sm:flex"
        aria-label={t("nav.ariaLabel")}
        data-testid="primary-nav"
      >
        {showServices && (
          <ServicesDropdown lang={lang} onNavigate={onMobileClose} />
        )}
        {showFeatures && (
          <FeaturesDropdown lang={lang} onNavigate={onMobileClose} />
        )}
        {visibleLinks.map((l) => renderLink(l))}
      </nav>
      {mobileOpen && (
        <nav
          className="order-last flex w-full flex-col gap-1 border-t border-border pt-3 sm:hidden"
          aria-label={t("nav.ariaLabel")}
          data-testid="primary-nav-mobile"
        >
          {showServices && (
            <ServicesDropdown lang={lang} onNavigate={onMobileClose} />
          )}
          {showFeatures && (
            <FeaturesDropdown lang={lang} onNavigate={onMobileClose} />
          )}
          {visibleLinks.map((l) => renderLink(l, "w-full"))}
        </nav>
      )}
    </>
  );
}

